import { useQuery } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "./api";
import type { RequestStatus, StatsBreakdown, StatsSummary, StatusCount, FeeBucket } from "./api";
import { dmy, rangeDays, rangeKey, rangeLabel, rangeQuery, type Range } from "./stats-range";

/**
 * ตัวดึงข้อมูล + ตัวแปลงของ Dashboard (ทุกระดับใช้จอเดียวกัน)
 *
 * ทุก endpoint กรองตาม scope ของผู้เรียกที่ server แล้วแคบลงด้วยตัวกรองพื้นที่ (Scope.NarrowTo)
 */

/** ช่วงยาวกว่านี้ กราฟแนวโน้มรวมเป็นรายสัปดาห์ และกราฟหมวดเป็นรายเดือน — รายวัน 365 จุดอ่านไม่ออก */
export const DAILY_LIMIT_DAYS = 92;
/** จำนวนชนิดเอกสารที่จอภาพรวมขอ */
export const DOC_TYPE_LIMIT = 8;
/** เกณฑ์ "ຊັກຊ້າ" ของพื้นที่ — เกณฑ์เดียวกับ KPI ค้างเกิน 7 วัน */
export const SLOW_THRESHOLD_SECONDS = 7 * 24 * 60 * 60;

export interface DailyRow {
  day: string;
  submitted: number;
  issued: number;
  fee_lak: number;
}
export interface DocTypeRow {
  document_type_id: string;
  code: string;
  name_lo: string;
  requests: number;
  issued: number;
}

/**
 * ขอบเขตที่กำลังดู (เจาะลง ນະຄອນ/ແຂວງ → ເມືອງ → ຕາແສງ → ບ້ານ) — แคบลงได้อย่างเดียว: server ตรวจว่า
 * อยู่ใน scope ของบัญชีเอง (นอกเขต = 403) ตัวเลือกที่จอเสนอจึงมาจาก /stats/breakdown กับ
 * /stats/places เท่านั้น ไม่เดาเอง · ระดับที่ลึกที่สุดที่ส่งไปคือขอบเขตจริง
 */
export interface PlaceFilter {
  province?: string;
  district?: string;
  tasaeng?: string;
  village?: string;
}

export const PLACE_LEVELS = ["province", "district", "tasaeng", "village"] as const;
export type PlaceLevel = (typeof PLACE_LEVELS)[number];

export const placeQuery = (
  f: PlaceFilter,
): { province_id?: string; district_id?: string; tasaeng_id?: string; village_id?: string } => ({
  ...(f.province ? { province_id: f.province } : {}),
  ...(f.district ? { district_id: f.district } : {}),
  ...(f.tasaeng ? { tasaeng_id: f.tasaeng } : {}),
  ...(f.village ? { village_id: f.village } : {}),
});

export const placeKey = (f: PlaceFilter) => PLACE_LEVELS.map((l) => f[l] ?? "-").join("/");

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** อ่าน/เขียนขอบเขตลง query string เหมือนช่วงเวลา — แชร์ลิงก์แล้วเห็นชุดเดียวกัน */
export function placeFromParams(p: URLSearchParams): PlaceFilter {
  const out: PlaceFilter = {};
  for (const l of PLACE_LEVELS) {
    const v = p.get(l) ?? "";
    if (UUID_RE.test(v)) out[l] = v;
  }
  return out;
}

export function placeToParams(f: PlaceFilter): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of PLACE_LEVELS) {
    const v = f[l];
    if (v) out[l] = v;
  }
  return out;
}

export interface PlaceOption {
  id: string;
  code: string;
  name_lo: string;
  parent_id: string;
  parent_name_lo: string;
}

/** ตัวเลือกของตัวกรอง — เฉพาะเมือง/ตาแสงที่อยู่ใน scope ของผู้เรียก */
export async function fetchPlaces(): Promise<{ districts: PlaceOption[]; tasaengs: PlaceOption[] }> {
  const { data, error, response } = await api.GET("/v1/admin/stats/places", { headers: authHeaders() });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export async function fetchSummary(r: Range, f: PlaceFilter): Promise<StatsSummary> {
  const { data, error, response } = await api.GET("/v1/admin/stats/summary", {
    params: { query: { ...rangeQuery(r), ...placeQuery(f) } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export async function fetchDaily(r: Range, f: PlaceFilter): Promise<DailyRow[]> {
  const { data, error, response } = await api.GET("/v1/admin/stats/daily", {
    params: { query: { ...rangeQuery(r), ...placeQuery(f) } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

export async function fetchStatusBreakdown(f: PlaceFilter): Promise<StatusCount[]> {
  const { data, error, response } = await api.GET("/v1/admin/stats/status-breakdown", {
    params: { query: placeQuery(f) },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

export async function fetchFees(r: Range, f: PlaceFilter, unit: "week" | "month"): Promise<FeeBucket[]> {
  const { data, error, response } = await api.GET("/v1/admin/stats/fees", {
    params: { query: { ...rangeQuery(r), ...placeQuery(f), unit } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

export async function fetchDocTypes(r: Range, f: PlaceFilter, limit = 6): Promise<DocTypeRow[]> {
  const { data, error, response } = await api.GET("/v1/admin/stats/document-types", {
    params: { query: { ...rangeQuery(r), ...placeQuery(f), limit } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

/** รวมรายวันเป็นก้อนละ 7 วัน (นับจากวันแรกของช่วง) สำหรับช่วงยาว */
export function bucketDaily(rows: DailyRow[], weekly: boolean): { start: string; end: string; row: DailyRow }[] {
  if (!weekly) return rows.map((r) => ({ start: r.day, end: r.day, row: r }));
  const out: { start: string; end: string; row: DailyRow }[] = [];
  for (let i = 0; i < rows.length; i += 7) {
    const chunk = rows.slice(i, i + 7);
    const first = chunk[0];
    const last = chunk[chunk.length - 1];
    if (!first || !last) continue;
    out.push({
      start: first.day,
      end: last.day,
      row: {
        day: first.day,
        submitted: chunk.reduce((s, r) => s + r.submitted, 0),
        issued: chunk.reduce((s, r) => s + r.issued, 0),
        fee_lak: chunk.reduce((s, r) => s + r.fee_lak, 0),
      },
    });
  }
  return out;
}

/** สีของโดนัทตามความหมายของสถานะ ไม่ใช่ตามลำดับ */
export function statusColor(status: RequestStatus): string {
  switch (status) {
    case "issued":
    case "signed":
    case "approved":
      return "var(--success)";
    case "rejected":
    case "cancelled":
      return "var(--error)";
    case "needs_changes":
      return "var(--warning)";
    case "draft":
      return "var(--ink-400)";
    case "in_review":
      return "var(--navy-700)";
    default:
      return "var(--navy-500)";
  }
}

/** ลำดับของสถานะในโดนัท — ตามเส้นทางของคำร้อง ไม่ใช่ตามจำนวน */
export const STATUS_ORDER: RequestStatus[] = [
  "draft", "submitted", "in_review", "needs_changes", "approved", "signed", "issued", "rejected", "cancelled",
];


export type BreakdownLevel = StatsBreakdown["level"];

/**
 * ยอดต่อพื้นที่ลูกของขอบเขตที่กำลังดู — ระดับลูกตัดสินที่ server จาก scope ที่แคบแล้ว
 * (system→ແຂວງ, ແຂວງ→ເມືອງ, ເມືອງ→ຕາແສງ, ຕາແສງ→ບ້ານ, ບ້ານ→none) · มีทุกพื้นที่ในเขตแม้ยอดเป็นศูนย์
 * และแถว place_id null = ระบุพื้นที่ไม่ได้ ต้องแสดง · Σ แถว = summary
 */
export async function fetchBreakdown(r: Range, f: PlaceFilter): Promise<StatsBreakdown> {
  const { data, error, response } = await api.GET("/v1/admin/stats/breakdown", {
    params: { query: { ...rangeQuery(r), ...placeQuery(f) } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

/** รายชั่วโมง (เวลาลาว) ของช่วง ມື້ນີ້ — 24 แถวเสมอ */
export async function fetchHourly(r: Range, f: PlaceFilter): Promise<HourlyRow[]> {
  const { data, error, response } = await api.GET("/v1/admin/stats/hourly", {
    params: { query: { ...rangeQuery(r), ...placeQuery(f) } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

export interface HourlyRow {
  hour: number;
  submitted: number;
  issued: number;
}

/** ข้อมูลชุดที่ทั้งจอเมืองและจอแขวงใช้: สรุป, รายวัน, สถานะตอนนี้, ชนิดเอกสาร */
export function useOverviewData(range: Range, place: PlaceFilter) {
  // key รวมทั้งช่วงเวลาและตัวกรอง — เปลี่ยนเมือง/ตาแสงแล้วต้องไม่ได้ cache ของชุดก่อน
  const key = `${rangeKey(range)}|${placeKey(place)}`;
  const summary = useQuery({ queryKey: ["stats", "summary", key], queryFn: () => fetchSummary(range, place) });
  const daily = useQuery({ queryKey: ["stats", "daily", key], queryFn: () => fetchDaily(range, place) });
  const statuses = useQuery({
    queryKey: ["stats", "status-breakdown", placeKey(place)],
    queryFn: () => fetchStatusBreakdown(place),
  });
  // 8 อันดับ = จำนวนแถวของการ์ด ບໍລິການທີ່ຖືກຮ້ອງຂໍຫຼາຍທີ່ສຸດ (โดนัทใช้ 5 แรก + ອື່ນໆ)
  const docTypes = useQuery({
    queryKey: ["stats", "document-types", key, DOC_TYPE_LIMIT],
    queryFn: () => fetchDocTypes(range, place, DOC_TYPE_LIMIT),
  });
  const weekly = rangeDays(range) > DAILY_LIMIT_DAYS;
  return { key, weekly, summary, daily, statuses, docTypes };
}

/** ช่วงที่กำลังดูเป็นข้อความ — ปุ่มด่วนใช้วันแรก/วันสุดท้ายจากข้อมูลรายวันที่ API ตอบ (ตรงกับที่นับจริง) */
export function rangeText(range: Range, rows: DailyRow[]): string {
  if (range.kind === "custom") return `${dmy(range.from)} – ${dmy(range.to)}`;
  const first = rows[0]?.day;
  const last = rows[rows.length - 1]?.day;
  if (range.kind === "today") return last ? `ມື້ນີ້ ${dmy(last)}` : "ມື້ນີ້";
  return first && last ? `${dmy(first)} – ${dmy(last)}` : rangeLabel(range);
}
